import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiGetMySubscription,
  getApiDateTimestamp,
  hasActiveMemberSubscription,
  MemberPaymentDetails,
  MemberSubscriptionDetails,
} from '../api/client';

export default function useMemberSubscriptionAccess() {
  const navigate = useNavigate();
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [subscription, setSubscription] = useState<MemberSubscriptionDetails | null>(null);
  const [latestPayment, setLatestPayment] = useState<MemberPaymentDetails | null>(null);
  const [subscriptionError, setSubscriptionError] = useState('');

  useEffect(() => {
    let active = true;

    const loadSubscription = async () => {
      setLoadingSubscription(true);
      setSubscriptionError('');

      try {
        const result = await apiGetMySubscription();

        if (!active) {
          return;
        }

        if (result.status === 401 || result.status === 403) {
          navigate('/member/login', { replace: true });
          return;
        }

        if (result.success) {
          setSubscription(result.subscription);
          setLatestPayment(result.latest_payment);
          return;
        }

        setSubscription(result.subscription ?? null);
        setLatestPayment(result.latest_payment ?? null);
        setSubscriptionError(result.message || 'Unable to load subscription details right now.');
      } finally {
        if (active) {
          setLoadingSubscription(false);
        }
      }
    };

    loadSubscription();

    return () => {
      active = false;
    };
  }, [navigate]);

  const expiresAtTimestamp = useMemo(
    () => getApiDateTimestamp(subscription?.expires_at),
    [subscription?.expires_at]
  );

  const hasArticleAccess = useMemo(
    () => hasActiveMemberSubscription(subscription),
    [subscription]
  );

  const isExpired = useMemo(() => {
    if (!subscription || expiresAtTimestamp === null) {
      return false;
    }

    return expiresAtTimestamp <= Date.now();
  }, [expiresAtTimestamp, subscription]);

  return {
    loadingSubscription,
    subscription,
    latestPayment,
    subscriptionError,
    hasArticleAccess,
    isExpired,
  };
}